import { transparentize } from 'polished';

const theme = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  fontWeight: {
    light: 300,
    regular: 400,
    bold: 500,
  },
  fontColor: 'rgb(9,30,66)',
  fontSize: {
    xxs: '12px',
    xs: '14px',
    sm: '16px',
    md: '18px',
    lg: '22px',
    xl: '24px',
  },
  spacing: {
    xxs: '4px',
    xs: '8px',
    sm: '12px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },
  shadow: {
    xs: '0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
    sm: '0px 4px 8px 0px rgba(0, 0, 0, 0.15)',
    md: '0px 8px 16px 0px rgba(0, 0, 0, 0.15)',
    lg: '0 3.2px 7.2px 0 rgba(0, 0, 0, .132),0 .6px 1.8px 0 rgba(0, 0, 0, .108)',
  },
  borderRadius: { xs: '3px', sm: '3px', md: '3px', lg: '3px', xl: '3px' },
  transition:
    'background 0.1s ease-out 0s, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38) 0s;',
  ToggleSwitch: {
    trackGap: '1px',
    width: '38px',
    height: '20px',
    borderRadius: '100px',
    trackSize: '14px',
    borderWidth: '1px',
    transition: '100ms cubic-bezier(0, 0, 0.35, 1)',
  },
};

const colors = {
  primary: '#0052CC',
  primaryDark: '#0065FF',
  primaryHover: '#1963D1',
  primaryHighlight: '#deecf9',
  hoverLight: '#E6E9EF',
  private: '#d13438',
  shareable: '#b4009e',
  black: '#000000',
  dark: '#1D1D1C',
  greyDark: '#6D778A',
  grey: '#DFE1E6',
  greyActive: '#F4F5F7',
  greyLight: '#EBECF0',
  greyLighting: '#FAFBFC',
  light: '#F5F6F8',
  white: '#FFFFFF',
  eggYolk: '#FFCB00',
  green: '#00C875',
  greenMiddle: '#027D00',
  greenDark: '#007000',
};

const components = {
  globalStyle: {
    body: {
      fontSize: theme.fontSize.xs,
      color: theme.fontColor,
    },
  },
  Button: {
    default: {
      borderRadius: theme.borderRadius.xs,
      fontWeight: theme.fontWeight.bold,
    },
    sizes: {
      sm: {
        fontSize: theme.fontSize.xs,
        height: 32,
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
      },
      md: {
        fontSize: theme.fontSize.xs,
        height: 32,
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
      },
      lg: {
        fontSize: theme.fontSize.xs,
        height: 32,
        paddingLeft: theme.spacing.sm,
        paddingRight: theme.spacing.sm,
      },
    },
  },
  Title: {
    default: {
      fontWeight: theme.fontWeight.bold,
      lineHeight: '100%',
    },
    sizes: {
      sm: {
        fontSize: theme.fontSize.sm,
      },
      md: {
        fontSize: theme.fontSize.md,
      },
      lg: {
        fontSize: theme.fontSize.lg,
      },
    },
  },
  Card: {
    default: {
      padding: theme.spacing.sm,
      borderRadius: theme.borderRadius.xs,
      boxShadow: theme.shadow.xs,
    },
    pressable: {
      border: 0,
      ':hover': {
        boxShadow: theme.shadow.lg,
      },
    },
  },
  AddCard: {
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.bold,
  },
  TextField: {
    borderRadius: theme.borderRadius.xs,
    borderWidth: 2,
    height: 38,
    fontSize: theme.fontSize.xs,
    outline: 0,
    transition:
      'background-color 0.2s ease-in-out 0s, border-color 0.2s ease-in-out 0s',
    ':hover': {
      backgroundColor: colors.greyLight,
    },
    ':focus': {
      backgroundColor: colors.white,
    },
  },
  AddButton: {
    fontSize: theme.fontSize.xs,
  },
  StepBar: {
    Container: {
      boxShadow: 'none',
    },
    StepLabel: {
      fontSize: theme.fontSize.xs,
    },
  },
  ColumnBoard: {
    boxShadow: 'none',
    borderRadius: theme.borderRadius.xs,
  },
  ActionItem: {
    container: {
      borderRadius: `${theme.borderRadius.xs} 0 0 ${theme.borderRadius.xs}`,
      borderLeftWidth: 2,
    },
    button: {
      borderRadius: `0 ${theme.borderRadius.xs} ${theme.borderRadius.xs} 0`,
    },
  },
  CardSummary: {
    container: {},
    number: {
      fontSize: 50,
    },
  },
  StatusBadge: {
    padding: '3px 6px',
  },
};

const lightThemeJIRA = {
  ...theme,
  palette: {
    ...colors,
    bgHoverGrey: colors.hoverLight,
    bgBody: colors.white,
    popUp: colors.white,
    colorBody: colors.dark,
    bgBodyTransparent: transparentize(0.2, colors.white),
    bgCard: colors.white,
    borderCard: colors.greyLight,
    borderCardHover: colors.primary,
    textContrast1: colors.dark,
    textContrast2: colors.greyDark,
    textContrast3: colors.grey,
    textLink: colors.primary,
    textLinkHover: colors.primaryHover,
    titleCard: colors.dark,
    titleCardHover: colors.primary,
    bgInvertBody: colors.greyLight,
    colorInvertBody: colors.black,
    bgButtonPrimary: colors.primary,
    bgButtonPrimaryHover: colors.primaryHover,
    bgButtonPrimaryActive: colors.primaryHover,
    bgButtonPrimaryDisabled: colors.greyLight,
    colorButtonPrimary: colors.white,
    colorButtonPrimaryDisabled: colors.grey,
    bgButtonSecondary: 'transparent',
    bgButtonSecondaryHover: colors.greyLight,
    bgButtonSecondaryActive: colors.primaryHighlight,
    bgButtonSecondaryDisabled: 'transparent',
    colorButtonSecondary: colors.primary,
    colorButtonSecondaryDisabled: colors.grey,
    bgInput: colors.greyLighting,
    colorInput: colors.dark,
    borderInput: colors.grey,
    borderInputFocus: colors.primary,
    borderInputButton: colors.primary,
    borderInputButtonHover: colors.primaryHover,
    bgColumnBoard: colors.greyLight,
    bgColumnBoardDragging: colors.primaryHighlight,
    colorStepbarLine: colors.greyLight,
    colorStepbarLineActive: colors.primary,
    bgCardBoard: colors.white,
    bgCardBoardDragging: colors.light,
    bgCardBoardCombining: colors.primary,
    colorAddButton: colors.greyDark,
    colorAddButtonHover: colors.primary,
    bgCardIdea: colors.white,
    bgCardIdeaDragging: colors.greyLight,
    bgCardIdeaCombining: colors.primary,
    colorCardIdeaIcon: colors.greyDark,
    colorCardIdea: colors.dark,
    colorCardIdeaCombining: colors.white,
    bgActivityInput: colors.white,
    bgActivityInputReadyOnly: colors.light,
    colorActivityInput: colors.dark,
    colorActivityInputDisabled: colors.grey,
    borderActivityInput: colors.greyLight,
    bgDropdown: colors.white,
    bgDropdownItemHover: colors.greyLight,
    colorDropdownItem: colors.dark,
    colorDropdownItemHover: colors.dark,
    badgeNStarted: colors.greyLight,
    badgeInProgress: colors.eggYolk,
    badgeDone: colors.green,
    danger: colors.private,
    switchColor: colors.greyDark,
    switchCheckedColor: colors.greenMiddle,
    switchCheckedDisabledColor: colors.greenMiddle,
    switchTrackColor: colors.white,
    switchTrackCheckedColor: colors.white,
    switchBorderColor: colors.greyDark,
  },
  selectStyle: {
    control: (provided, state) => ({
      ...provided,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      pointerEvents: 'auto',
      height: 38,
      paddingLeft: 8,
      borderRadius: theme.borderRadius.sm,
      borderWidth: 0,
      ':hover': {
        backgroundColor: state.isFocused ? colors.white : colors.greyLight,
      },
      backgroundColor: state.isFocused ? colors.white : colors.greyActive,
      transition:
        'background-color 0.2s ease-in-out 0s, border-color 0.2s ease-in-out 0s',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: 8,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: 8,
      marginBottom: 8,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: 8,
    }),
    multiValue: (provided) => ({
      ...provided,
      borderRadius: 2,
      margin: 2,
    }),
    menu: (provided) => ({
      ...provided,
      paddingTop: 4,
      paddingBottom: 4,
    }),
    option: (provided) => ({
      ...provided,
      padding: '6px 10px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      padding: '0 2px',
    }),
  },
  components,
};

export { lightThemeJIRA };
